import { Stack, useMantineTheme } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { IconLaurelWreath1 } from '@tabler/icons-react'
import confetti from 'canvas-confetti'
import React, { useCallback, useRef } from 'react'
import { useAccount } from 'wagmi'

import ProfilesCard from '@/components/Profile/ProfilesCard'
import { ProcessedBalance } from '@/utils/utils'

import NominatedBy from './NominatedBy'
import VotesCard from './VotesCard'

interface ElectionsWinnerProps {
  nominee: string
  nominator: string
  userVoteTotals: { [key: string]: ProcessedBalance }
  candidateVoteTotals: { [key: string]: ProcessedBalance }
  isLoading: boolean
}

const ElectionsWinner: React.FC<ElectionsWinnerProps> = ({
  nominee,
  nominator,
  userVoteTotals,
  candidateVoteTotals,
  isLoading,
}) => {
  const { address } = useAccount()
  const theme = useMantineTheme()
  const isBreakpoint = useMediaQuery(`(max-width: ${theme.breakpoints.md})`)
  const confettiRan = useRef(false)
  // Reference to the parent element of the Item Card
  const parentRef = useRef<HTMLDivElement>(null)

  const runConfetti = useCallback(() => {
    if (confettiRan.current) {
      return
    }
    confettiRan.current = true
    confetti({
      particleCount: 1550,
      spread: 1400,
      zIndex: 201,
    })
    // launch a few confetti from the left edge
    confetti({
      particleCount: 117,
      angle: 60,
      spread: 55,
      origin: { x: 0 },
      zIndex: 201,
    })
    // and launch a few from the right edge
    confetti({
      particleCount: 117,
      angle: 120,
      spread: 55,
      origin: { x: 1 },
      zIndex: 201,
    })
  }, [confetti, confettiRan])

  return (
    <Stack gap="15px">
      <Stack
        style={{
          position: 'relative',
          border: `1px solid ${theme.colors.barChartYellow[0]}`,
          borderRadius: '12px',
          backgroundColor: 'rgba(234, 183, 0, 0.61)',
        }}
        // on hover
        onMouseEnter={runConfetti}
        onClick={() => {
          confettiRan.current = false
          runConfetti()
        }}
      >
        <ProfilesCard
          profileAddress={nominee}
          parentRef={parentRef}
          extraInfo={<NominatedBy nominator={nominator} />}
        />
        <IconLaurelWreath1
          color={theme.colors.barChartYellow[0]}
          style={{ position: 'absolute', top: '110px', left: '75px' }}
        />
      </Stack>
      <Stack
        style={{
          flexDirection: isBreakpoint ? 'column' : 'row',
          width: '100%',
          gap: '15px',
        }}
      >
        {address && (
          <VotesCard
            index={0}
            phase="results"
            nominee={nominee}
            isBreakpoint
            type="yours"
            votes={userVoteTotals[nominee]}
            loading={isLoading}
            removed={false}
          />
        )}
        <VotesCard
          index={0}
          phase="results"
          nominee={nominee}
          isBreakpoint
          type="all"
          votes={candidateVoteTotals[nominee]}
          loading={isLoading}
          removed={false}
        />
      </Stack>
    </Stack>
  )
}

export default ElectionsWinner
